// pending-0.1.19 bump for ChangeOfContact chain port
/**
 * ContactChangeOrchestrator — RCM change-of-contact side-effect chain.
 *
 * Fires the downstream side-effects that SPFx CifUpdateScreen used to
 * handle inline after a successful CustMod when the diff touched a contact
 * field:
 *
 *   (1. 30-day "Address Verify" task and 2. ADDRESS_CHANGE_NOTICE_V1 letter: removed
 *    2026-10-27 per Jessica Beucler, CRO. Core produces the change-of-address notice
 *    and Risk mails it; no re-verification is required beyond the initial one.)
 *   3. Fraud scan (compliance)
 *        - Cross-references new contact values vs. insider employee CIFs +
 *          recent login/auth activity on the previous contact.
 *        - Server returns { createdTaskIds } for any FraudAlert tasks opened.
 *        - Idempotent at the server, but we still skip if a prior open
 *          FraudAlert task for this cifId is already present.
 *
 * SPFx source this replaces:
 *   spfx-account-opening-v2/.../components/screens/CifUpdateScreen.tsx:328-362
 *     (insider detection + FraudAlert task creation only — the 30-day
 *     tickler and the physical notice never made it into the SPFx build;
 *     they're called out as missing items #1 and #2 in
 *     docs/audit/workflow-deep-dives/cif-update-workflow.md:16-24.)
 *
 * Design notes:
 *   - Side-effects are fire-and-continue: a failure in one does not
 *     abort the others. Errors are returned in the result bag so the caller
 *     (CifEditPanel) can surface them as a non-blocking warning Banner.
 *   - CustMod has already landed before this runs — we never undo the edit.
 */
import type { ApiClient } from '@retail-ops/api-client';
import type { ChangeDiffEntry } from '../hooks/useChangeTracker';

// -----------------------------------------------------------------------------
// Contact-info field keys (CifEditPanel field `key` shape)
// -----------------------------------------------------------------------------

/**
 * Subset of the CifEditPanel FIELDS[] keys that count as "contact info" for the
 * purposes of the ChangeOfContact side-effect chain. If the diff touches
 * nothing in this set, the orchestrator is a no-op.
 *
 * Aligned with SPFx CifUpdateScreen.tsx:329
 *   const contactFields = ['addr','city','state','zip','phoneHome','phoneCell','phoneBus','email'];
 * mapped into v3 FIELDS[].key names.
 */
export const CONTACT_INFO_FIELD_KEYS: ReadonlyArray<string> = [
  'StreetAddr1',
  'MailAddr',
  'City',
  'MailCity',
  'State',
  'MailState',
  'Zip',
  'MailZip',
  'PhoneHome',
  'PhoneCell',
  'PhoneBus',
  'PhoneOther',
  'Email',
  'Email2',
];

/** True iff at least one contact-info field is present in the diff. */
export function diffTouchesContactInfo(diff: ReadonlyArray<ChangeDiffEntry>): boolean {
  const set = new Set(CONTACT_INFO_FIELD_KEYS);
  return diff.some((d) => set.has(d.field));
}

// -----------------------------------------------------------------------------
// Public input / output shapes
// -----------------------------------------------------------------------------

export interface ContactChangeRunInput {
  /** Customer CIF this change applies to. */
  cifId: string;
  /** Baseline (before) — same shape CifEditPanel passes to useChangeTracker. */
  before: Readonly<Record<string, string>>;
  /** Working (after) — post-save working copy. */
  after: Readonly<Record<string, string>>;
  /** Officer making the change, forwarded as task createdByUser + assignedTo. */
  officerCode: string;
  /** Diff from useChangeTracker (reused to avoid recomputing). */
  diff: ReadonlyArray<ChangeDiffEntry>;
}

export interface ContactChangeRunResult {
  /** Whether any side-effect actually ran (i.e. a contact field changed). */
  ran: boolean;
  /** Task IDs opened by /v3/compliance/fraud-scan, if any. */
  fraudAlertTaskIds?: ReadonlyArray<string>;
  /** True when a prior open FraudAlert task pre-empted scan dispatch. */
  fraudScanDeduped?: boolean;
  /** Per-step errors (non-fatal). Caller can surface as a warning. */
  errors: ReadonlyArray<{ step: string; message: string }>;
}

// -----------------------------------------------------------------------------
// ContactChangeOrchestrator
// -----------------------------------------------------------------------------

export class ContactChangeOrchestrator {
  public constructor(private readonly client: ApiClient) {}

  public async runContactChangeEffects(
    input: ContactChangeRunInput,
  ): Promise<ContactChangeRunResult> {
    const errors: Array<{ step: string; message: string }> = [];

    if (!diffTouchesContactInfo(input.diff)) {
      return { ran: false, errors: [] };
    }

    const out: ContactChangeRunResult = { ran: true, errors };

    // The 30-day "Address Verify" task and the ADDRESS_CHANGE_NOTICE_V1 letter to the
    // old address were removed 2026-10-27 (Bill; Jessica Beucler, CRO, 10/27 2:23 PM and
    // 3:25 PM): Core produces the change-of-address notice when the change reaches Core
    // and Risk Management mails it, and "there is not a re-verification that has to be
    // done, just the initial verification" (Beneficial Ownership excepted). The notice
    // template was never seeded, so it had never sent.

    // ---------------------------------------------------------------------
    // Fraud scan (compliance)
    // Fires on ANY contact-info change (insider match can happen on phone or
    // email alone — cf. SPFx CifUpdateScreen.tsx:329-339).
    // ---------------------------------------------------------------------
    try {
      const priorFraud = await this.client.retail.tasks.list({
        cifId: input.cifId,
        type: 'FraudAlert',
        status: 'Open',
      });
      const hasOpenFraud = (priorFraud?.tasks?.length ?? 0) > 0;
      if (hasOpenFraud) {
        out.fraudScanDeduped = true;
      } else {
        const scan = await this.client.retail.compliance.fraudScan({
          cifId: input.cifId,
          changeType: 'contact',
          changedFields: input.diff
            .map((d) => d.field)
            .filter((k) => CONTACT_INFO_FIELD_KEYS.includes(k)),
          before: pickFields(input.before, CONTACT_INFO_FIELD_KEYS),
          after: pickFields(input.after, CONTACT_INFO_FIELD_KEYS),
          officerCode: input.officerCode,
        });
        if (scan?.createdTaskIds?.length) {
          out.fraudAlertTaskIds = scan.createdTaskIds;
        }
      }
    } catch (err) {
      errors.push({ step: 'FraudScan', message: messageOf(err) });
    }

    return out;
  }
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

function pickFields(
  rec: Readonly<Record<string, string>>,
  keys: ReadonlyArray<string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of keys) {
    if (rec[k] !== undefined) out[k] = rec[k];
  }
  return out;
}
